import { useDispatch, useSelector } from "react-redux";

import type {
    BaseActivatedPanelProps,
    SelectionDropdownItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { ManualSelectionItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import {
    selectActiveLeadsType,
    selectSelectedRowDomains,
    setIsRowSelectionEnabled,
    useClearAndCloseRowsSelection,
} from "app/pages/sales-intelligence/modules/leads-search";
import { moveLeadsToListThunk } from "app/pages/sales-intelligence/modules/leads-search/store/effects";
import { useListParams } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import { moveTopNLeadsToListThunk } from "app/pages/sales-intelligence/pages/opportunity-list/store/effects";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import SaveToListPanel from "app/pages/sales-intelligence/sub-modules/opportunities/components/table-actions-panel/SaveToListPanel/SaveToListPanel";
import useCurrentStaticList from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useCurrentStaticList";
import useTrackedWebsitesQuota from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useTrackedWebsitesQuota";
import saveToListSelectionDropdown from "app/pages/sales-intelligence/sub-modules/opportunities/models/save-to-list-selection/saveToListSelectionDropdown";
import {
    selectOpportunityListCreating,
    selectOpportunityListUpdating,
    selectSortedOpportunityLists,
} from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import type {
    MoveToListResponseDto,
    OpportunityListType,
} from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { useLeadsResultsActionsState } from "../../../hooks/useLeadsResultsActionsState";

import { PANEL_TITLE_TRANSLATABLE } from "./const";

const MoveToListPanelContainer = (props: BaseActivatedPanelProps) => {
    const dispatch = useDispatch();

    const { id: listId } = useListParams();
    const currentList = useCurrentStaticList();

    const selectedRowDomains = useSelector(selectSelectedRowDomains);
    const activeLeadsType = useSelector(selectActiveLeadsType);
    const isListCreateInProgress = useSelector(selectOpportunityListCreating);
    const isListUpdateInProgress = useSelector(selectOpportunityListUpdating);
    const lists = useSelector(selectSortedOpportunityLists);

    const { areResultsFetching, areActionsDisabled, numberOfResults } = useLeadsResultsActionsState(activeLeadsType);

    const { remaining } = useTrackedWebsitesQuota();

    const clearAndCloseRowsSelection = useClearAndCloseRowsSelection();

    const handleDeactivate = (): void => {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    };

    const handleSelectionChange = (item: Readonly<SelectionDropdownItem>): void => {
        if (item.id === ManualSelectionItem.ID) {
            dispatch(setIsRowSelectionEnabled(true));

            return;
        }

        clearAndCloseRowsSelection();
    };

    const handleSubmit = async (
        targetList: Readonly<OpportunityListType>,
        selectionItem: Readonly<SelectionDropdownItem>,
    ): Promise<void> => {
        if (is.undefined(currentList)) {
            handleDeactivate();
            return;
        }

        let response: MoveToListResponseDto | undefined;
        if (selectionItem.id === ManualSelectionItem.ID) {
            response = await dispatch(moveLeadsToListThunk(currentList, targetList, selectedRowDomains));
        } else {
            response = await dispatch(
                moveTopNLeadsToListThunk({
                    currentList,
                    targetList,
                    domainsCount: selectionItem.value,
                }),
            );
        }

        if (!is.undefined(response)) {
            trackingService.trackMultiSelectorSuccessMoveToListAction(response.opportunities, targetList.friendlyName);
        }

        handleDeactivate();
    };

    return (
        <SaveToListPanel
            isDisabled={areActionsDisabled}
            isLoading={areResultsFetching}
            isSubmitInProgress={isListCreateInProgress || isListUpdateInProgress}
            lists={lists.filter((list) => list.opportunityListId !== listId)}
            numberOfRemainingItems={remaining}
            numberOfResults={numberOfResults}
            numberOfSelectedItems={selectedRowDomains.length}
            numberOfUntrackedSelectedItems={0}
            selectionDropdown={saveToListSelectionDropdown}
            panelTitleIconName="arrow-right"
            panelTitleLabel={PANEL_TITLE_TRANSLATABLE}
            onDeactivate={handleDeactivate}
            onSelectionChange={handleSelectionChange}
            onSubmit={handleSubmit}
        />
    );
};

export default MoveToListPanelContainer;
